/**
 * Spatial geometry helpers for polygons.
 *
 * Pure functions — no SDK coupling, no DOM, no side effects.
 *
 * @module searchResultsMap/services/geometry
 */

/**
 * Tests if a polygon's bounding box intersects the bounds provided.
 * Uses AABB (axis-aligned bounding box) overlap test.
 * Returns true when bounds is null/undefined.
 *
 * @param {Object} polygon - Polygon with topRightLatitude/Longitude, bottomLeftLatitude/Longitude
 * @param {{ north: number, south: number, east: number, west: number }|null} bounds
 * @returns {boolean}
 */
export function isPolygonInBounds(polygon, bounds) {
  if (!bounds) {
    return true;
  }
  return (
    polygon.topRightLatitude    >= bounds.south &&
    polygon.bottomLeftLatitude  <= bounds.north &&
    polygon.topRightLongitude   >= bounds.west  &&
    polygon.bottomLeftLongitude <= bounds.east
  );
}

/**
 * Tests if a point intersects the bounds provided.
 * Uses AABB (axis-aligned bounding box) overlap test.
 * Returns true when bounds is null/undefined.
 *
 * @param {Object} point - Point with latitude and longitude
 * @param {{ north: number, south: number, east: number, west: number }|null} bounds
 * @returns {boolean}
 */
export function isPointInBounds(point, bounds) {
  if (!bounds) {
    return true;
  }

  return (
    point.lat  >= bounds.south &&
    point.lat  <= bounds.north &&
    point.lng  >= bounds.west &&
    point.lng  <= bounds.east
  );
}

/**
 * Merges two sets of coordinate bounds by taking the union of both rectangles.
 * The result encompasses both the base bounds and the extension bounds.
 *
 * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }} baseBounds
 * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }} extensionBounds
 * @returns {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }}
 */
export function mergeBounds(baseBounds, extensionBounds) {
  return {
    minLatitude: Math.min(baseBounds.minLatitude, extensionBounds.minLatitude),
    maxLatitude: Math.max(baseBounds.maxLatitude, extensionBounds.maxLatitude),
    minLongitude: Math.min(baseBounds.minLongitude, extensionBounds.minLongitude),
    maxLongitude: Math.max(baseBounds.maxLongitude, extensionBounds.maxLongitude),
  };
}

/**
 * Tests whether a set of coordinate bounds forms a valid, non-degenerate rectangle.
 * Requires all four edges to be finite numbers with north strictly greater than south
 * and east strictly greater than west.
 *
 * @param {{ north: number, south: number, east: number, west: number }|null} bounds
 * @returns {boolean}
 */
export function areBoundsValid(bounds) {
  if (!bounds) {
    return false;
  }

  const { north, south, east, west } = bounds;

  if (![north, south, east, west].every(Number.isFinite)) {
    return false;
  }

  return north > south && east > west;
}

/**
 * Returns how much the viewport has drifted away from bounds — the area covering the
 * original search's farthest markers — expressed as a ratio between 0 (no drift) and
 * 1 (no overlap left at all).
 *
 * Uses an AABB intersection to compute the overlapping area, then expresses the
 * non-overlapping remainder as a ratio of the SMALLER of the two rectangles' area.
 * Neither rectangle alone works as the denominator:
 * - Using only viewport's area falsely reports drift at rest / on zoom-out, since a
 *   viewport is routinely larger than a tight backend-suggested result area even when
 *   the user hasn't moved — bounds would only ever fill a fraction of it.
 * - Using only bounds' area falsely reports drift on zoom-in, since a viewport fully
 *   contained within (but much smaller than) bounds is still bounds "mostly not
 *   visible", even though the user is still squarely inside known territory.
 * Normalizing against whichever rectangle is smaller means the ratio is 0 whenever
 * one rectangle fully contains the other (pure zoom in/out, no drift), and only rises
 * once the two rectangles genuinely stop overlapping — i.e. the user is heading into
 * new territory beyond the original search's known area.
 *
 * Returns 1 (fully outside) when either rectangle is invalid or degenerate, or when
 * there is no overlap at all. Returns 0 when either rectangle fully contains the other.
 *
 * @param {{ north: number, south: number, east: number, west: number }} viewport
 * @param {{ north: number, south: number, east: number, west: number }} bounds
 * @returns {number} Ratio between 0 (no drift) and 1 (fully outside)
 */
export function getOutsideAreaRatio(viewport, bounds) {
  if (!areBoundsValid(viewport) || !areBoundsValid(bounds)) {
    return 1;
  }

  const overlapNorth = Math.min(viewport.north, bounds.north);
  const overlapSouth = Math.max(viewport.south, bounds.south);
  const overlapEast = Math.min(viewport.east, bounds.east);
  const overlapWest = Math.max(viewport.west, bounds.west);

  const overlapHeight = Math.max(0, overlapNorth - overlapSouth);
  const overlapWidth = Math.max(0, overlapEast - overlapWest);
  const overlapArea = overlapHeight * overlapWidth;

  const viewportArea = (viewport.north - viewport.south) * (viewport.east - viewport.west);
  const boundsArea = (bounds.north - bounds.south) * (bounds.east - bounds.west);
  const smallerArea = Math.min(viewportArea, boundsArea);

  return 1 - (overlapArea / smallerArea);
}

/**
 * Returns true when a camera snapshot differs from a previously recorded one.
 * Compares zoom and all four bound edges with strict equality.
 * Returns true when either snapshot is absent so the first render is never skipped.
 *
 * @param {{ zoom: number, bounds: { north: number, south: number, east: number, west: number }|null }|null} previous
 * @param {{ zoom: number, bounds: { north: number, south: number, east: number, west: number }|null }|null} current
 * @returns {boolean}
 */
export function hasCameraChanged(previousCamera, currentCamera) {
  if (!previousCamera || !currentCamera) {
    return true;
  }

  if (previousCamera.zoom !== currentCamera.zoom) {
    return true;
  }
  
  const previousBounds = previousCamera.bounds;
  const currentBounds = currentCamera.bounds;

  if (!previousBounds && !currentBounds) {
    return false;
  }

  if (!previousBounds || !currentBounds) {
    return true;
  }

  return (
    previousBounds.north !== currentBounds.north ||
    previousBounds.south !== currentBounds.south ||
    previousBounds.east !== currentBounds.east ||
    previousBounds.west !== currentBounds.west
  );
}